import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { studioDate } from '../../utils/studioDate';
import { AdminSection } from './AdminPage';
import { toShootTab, type ShootTab } from './shootTab';
import { dateAttention, depositIsIn, todayShootLine } from '../../utils/todayDesk';
import { deliveryCountdown, deriveClientJourney, studioToday, type JourneyStage } from '../../utils/clientJourney';
import type { StoredQuote } from '../../utils/quoteRecord';

interface QuoteRow {
  id: string;
  quoteNumber: string;
  status: string;
  updatedAt: string;
  shootType?: string;
  preferredDate?: string;
  preferredTime?: string;
  preferredLocation?: { name?: string; address?: string; mapsUrl?: string };
  client?: { name?: string; phoneE164?: string; email?: string; preference?: string };
  archivedAt?: string | null;
  deletedAt?: string | null;
  isTest?: boolean;
  activity?: Array<{ at: string; action: string; by?: string }>;
}

interface PaymentSummary {
  quoteId: string;
  lockedMinor: number;
  receivedMinor: number;
  outstandingMinor: number;
  currentlyDueMinor?: number;
  overdueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
  figures?: { overdueMinor?: number };
}

interface ReminderRow {
  id: string;
  category: string;
  label: string;
  quoteId: string;
  clientName: string;
  reason: string;
  noticeType?: string;
  link?: string;
  /** Reminders for Carmen herself (shoot passed, editing due): nothing is sent to the client. */
  internal?: boolean;
  action?: string;
  daysLeft?: number | null;
  overdue?: boolean;
}

interface Attention {
  awaitingCarmen: number;
  notificationFailures: number;
  datesToConfirmIds?: string[];
  failedNoticeQuoteIds?: string[];
  invoicesAwaitingCapture?: number;
  shootPassedIds?: string[];
  awaitingDeliveryIds?: string[];
  deliveryOverdueIds?: string[];
  closeReadyIds?: string[];
}

interface ActivityRow {
  id: string;
  at: string;
  kind: string;
  actor: 'client' | 'studio' | 'system';
  quoteId: string;
  quoteNumber?: string;
  clientName: string;
  title: string;
  detail?: string;
  tab?: ShootTab;
}

interface BookingRow {
  quoteId: string;
  /** Set when the overview has already split a multi-day job into one row per day. */
  sessionId?: string;
  clientName?: string;
  shootDate?: string;
  startTime?: string;
  endTime?: string;
  locationName?: string;
  mapsUrl?: string;
  shootType?: string;
  dateConfirmed?: boolean;
  completedAt?: string;
  deliveryDueOn?: string;
  deliveredAt?: string;
  deliveryLink?: string;
  closedAt?: string;
  deletedAt?: string | null;
  archivedAt?: string | null;
  isTest?: boolean;
  sessions?: Array<{ id?: string; shootDate?: string; startTime?: string; endTime?: string; label?: string }>;
}

interface EnquiryRow {
  id: string;
  name: string;
  source?: string;
  shootType?: string;
  preferredDate?: string;
  receivedAt?: string;
  linkedQuoteId?: string | null;
}

interface DeskItem {
  id: string;
  client: string;
  reason: string;
  /** Shoot type, date and place, shown above the next step. */
  detail?: string;
  /** Opens this shoot on the tab where the work is done. */
  tab?: ShootTab;
  /** Or, for things that are not about one shoot, a whole section. */
  section?: AdminSection;
  quoteId?: string;
}

const operational = (quote: { archivedAt?: string | null; deletedAt?: string | null; isTest?: boolean }) => (
  !quote.archivedAt && !quote.deletedAt && !quote.isTest
);

function greeting(now: Date) {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function arrived(value?: string) {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short' }).format(date);
}

function shortDay(value?: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!match) return '';
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
}

function withAmounts(reason: string) {
  return reason.split(/(R\s[\d,]+\.\d{2})/g).map((part, index) => (
    /R\s[\d,]+\.\d{2}/.test(part) ? <span className="studio-amount" key={index}>{part}</span> : part
  ));
}

function timeAgo(value: string, now: Date) {
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '';
  const minutes = Math.round((now.getTime() - ms) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short' }).format(new Date(ms));
}

/** Pipeline columns on Today: every live job sits in exactly one. */
const PIPELINE: Array<{ stage: JourneyStage; label: string; hint: string }> = [
  { stage: 'enquiry', label: 'Client details', hint: 'No quotation yet' },
  { stage: 'quoted', label: 'Quoted', hint: 'With the client' },
  { stage: 'accepted', label: 'Accepted', hint: 'Deposit or date to settle' },
  { stage: 'booked', label: 'Booked', hint: 'Date confirmed' },
  { stage: 'editing', label: 'Editing', hint: 'Client waiting' },
  { stage: 'delivered', label: 'Delivered', hint: 'Close when paid' },
];

const Dashboard = ({ onOpen, onOpenShoot, onOpenEnquiry, onStartEnquiry }: {
  onOpen: (section: AdminSection, quoteId?: string) => void;
  onOpenShoot?: (quoteId: string, tab?: ShootTab) => void;
  onOpenEnquiry?: (enquiryId: string) => void;
  onStartEnquiry?: () => void;
}) => {
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [enquiries, setEnquiries] = useState<EnquiryRow[]>([]);
  const [payments, setPayments] = useState<PaymentSummary[]>([]);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [attention, setAttention] = useState<Attention | null>(null);
  const [billing, setBilling] = useState<{ invoicesAwaitingCapture?: number; paymentsNotRecordedInZoho?: number } | null>(null);
  const [reminders, setReminders] = useState<ReminderRow[]>([]);
  const [reminderNote, setReminderNote] = useState('');
  const [showAllAttention, setShowAllAttention] = useState(false);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [error, setError] = useState('');
  const [confirmRemoveId, setConfirmRemoveId] = useState('');
  const [removingEnquiry, setRemovingEnquiry] = useState('');
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const today = studioToday(now);

  const loadReminders = async () => {
    const response = await fetch(`${API_BASE_URL}/studio/reminders`);
    if (!response.ok) {
      setReminders([]);
      return;
    }
    const body = await response.json();
    setReminders(Array.isArray(body.suggestions) ? body.suggestions : []);
  };

  const openReminderShoot = (row: ReminderRow) => {
    if (onOpenShoot) {
      onOpenShoot(row.quoteId, row.internal ? 'money' : 'conversation');
      return;
    }
    onOpen('clients', row.quoteId);
  };

  const actOnReminder = async (action: 'snooze' | 'dismiss', row: ReminderRow) => {
    const response = await fetch(`${API_BASE_URL}/studio/reminders/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quoteId: row.quoteId, category: row.category }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'The reminder action could not be completed.');
      return;
    }
    setReminderNote(action === 'snooze' ? `${row.clientName}: reminder snoozed.` : `${row.clientName}: reminder dismissed.`);
    await loadReminders();
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/studio/overview`, { cache: 'no-store' });
        if (!response.ok) throw new Error('The studio overview could not be loaded.');
        const body = await response.json();
        if (!active) return;
        setQuotes(Array.isArray(body.quotes) ? body.quotes : []);
        setEnquiries(Array.isArray(body.enquiries) ? body.enquiries : []);
        setPayments(Array.isArray(body.payments) ? body.payments : []);
        setAttention(body.attention || null);
        setBookings(Array.isArray(body.bookings) ? body.bookings : []);
        setBilling(body.billing || null);
        setReminders(Array.isArray(body.reminders) ? body.reminders : []);
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'The studio overview could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    const loadActivity = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/studio/activity?limit=12`);
        if (!response.ok) return;
        const body = await response.json();
        if (active) setActivity(Array.isArray(body) ? body : []);
      } catch (_) {
        // The feed is a convenience; the page stands without it.
      }
    };
    load();
    loadActivity();
    return () => {
      active = false;
    };
  }, []);

  const byId = new Map(quotes.map((quote) => [quote.id, quote]));
  const activePayments = payments.filter((summary) => {
    const quote = byId.get(summary.quoteId);
    return quote ? operational(quote) : false;
  });
  const money = activePayments.reduce((total, summary) => ({
    agreed: total.agreed + (summary.lockedMinor || 0),
    received: total.received + (summary.receivedMinor || 0),
    outstanding: total.outstanding + (summary.outstandingMinor || 0),
    overdue: total.overdue + (summary.figures?.overdueMinor || summary.overdueMinor || 0),
  }), { agreed: 0, received: 0, outstanding: 0, overdue: 0 });

  const items: DeskItem[] = [];
  const seen = new Set<string>();
  const push = (item: DeskItem) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    items.push(item);
  };
  const forShoot = (quote: QuoteRow | undefined, tab: ShootTab) => ({
    client: quote?.client?.name || quote?.quoteNumber || 'Client',
    quoteId: quote?.id,
    tab,
  });
  const shootLine = (quote?: QuoteRow) => {
    const booking = quote ? bookings.find((row) => row.quoteId === quote.id) : undefined;
    const enquiry = quote ? enquiries.find((row) => row.linkedQuoteId === quote.id) : undefined;
    const place = [quote?.preferredLocation?.name, quote?.preferredLocation?.address].filter(Boolean).join(', ');
    return todayShootLine({
      shootType: booking?.shootType || quote?.shootType || enquiry?.shootType,
      date: booking?.shootDate || quote?.preferredDate || enquiry?.preferredDate,
      time: booking?.startTime || quote?.preferredTime,
      place: booking?.locationName || place,
    }, { fill: true });
  };
  (attention?.datesToConfirmIds || []).forEach((id) => {
    const quote = byId.get(id);
    if (!quote || !operational(quote)) return;
    const summary = activePayments.find((row) => row.quoteId === id);
    if (!depositIsIn(summary)) return;
    const booking = bookings.find((row) => row.quoteId === id);
    const enquiry = enquiries.find((row) => row.linkedQuoteId === id);
    push({
      id: `confirm:${id}`,
      reason: dateAttention({
        hasDate: Boolean(booking?.shootDate || quote.preferredDate || enquiry?.preferredDate),
        depositReceived: true,
      }),
      detail: shootLine(quote),
      ...forShoot(quote, 'booking'),
    });
  });
  quotes.filter((quote) => operational(quote) && attention?.failedNoticeQuoteIds?.includes(quote.id)).forEach((quote) => {
    push({ id: quote.id, reason: 'A message to this client did not go through', detail: shootLine(quote), ...forShoot(quote, 'conversation') });
  });
  if ((attention?.awaitingCarmen || 0) > 0) {
    push({ id: 'replies', client: 'Messages', reason: `${attention?.awaitingCarmen} waiting for your reply`, section: 'client-messages' });
  }
  activePayments.filter((summary) => (summary.figures?.overdueMinor || summary.overdueMinor || 0) > 0).forEach((summary) => {
    const quote = byId.get(summary.quoteId);
    const amount = summary.figures?.overdueMinor || summary.overdueMinor || 0;
    push({ id: summary.quoteId, reason: `Payment overdue: ${formatQuoteMoney(amount, 'R')}`, detail: shootLine(quote), ...forShoot(quote, 'money') });
  });
  (attention?.datesToConfirmIds || []).forEach((id) => {
    const quote = byId.get(id);
    if (!quote || !operational(quote) || seen.has(id)) return;
    const summary = activePayments.find((row) => row.quoteId === id);
    if (depositIsIn(summary)) return;
    const booking = bookings.find((row) => row.quoteId === id);
    const enquiry = enquiries.find((row) => row.linkedQuoteId === id);
    const deposit = summary && (summary.depositDueMinor || 0) > 0 ? summary : undefined;
    const detail = shootLine(quote);
    const reason = dateAttention({
      hasDate: Boolean(booking?.shootDate || quote.preferredDate || enquiry?.preferredDate),
      deposit: deposit ? `Deposit of ${formatQuoteMoney(deposit.depositDueMinor || 0, 'R')} not yet received` : '',
    });
    push({ id, reason, detail, ...forShoot(quote, 'booking') });
  });
  activePayments.filter((summary) => (summary.depositDueMinor || 0) > 0 && !seen.has(summary.quoteId)).forEach((summary) => {
    const quote = byId.get(summary.quoteId);
    push({
      id: summary.quoteId,
      reason: `Deposit of ${formatQuoteMoney(summary.depositDueMinor || 0, 'R')} not yet received`,
      detail: shootLine(quote),
      ...forShoot(quote, 'money'),
    });
  });
  // After the shoot: the date passed, the client is waiting for photographs, or the job can close.
  const liveBookings = bookings.filter((booking) => {
    const quote = byId.get(booking.quoteId);
    return Boolean(quote && operational(quote) && quote.status === 'accepted' && !booking.closedAt && !booking.deletedAt && !booking.archivedAt && !booking.isTest);
  });
  const seenEditing = new Set<string>();
  const editingJobs = bookings
    .filter((booking) => {
      if (!booking.completedAt || booking.deliveredAt || booking.closedAt || booking.deletedAt || booking.archivedAt) return false;
      if (seenEditing.has(booking.quoteId)) return false;
      seenEditing.add(booking.quoteId);
      const quote = byId.get(booking.quoteId);
      return !quote || (!quote.deletedAt && !quote.archivedAt);
    })
    .map((booking) => ({ booking, quote: byId.get(booking.quoteId), countdown: deliveryCountdown(booking.deliveryDueOn, today) }))
    .sort((left, right) => (left.countdown.daysLeft ?? 9999) - (right.countdown.daysLeft ?? 9999));
  editingJobs.filter((row) => row.countdown.overdue).forEach(({ booking, quote, countdown }) => {
    push({
      id: `deliver:${booking.quoteId}`,
      reason: `Delivery overdue — ${countdown.text}`,
      detail: shootLine(quote),
      ...forShoot(quote, 'money'),
    });
  });
  liveBookings
    .filter((booking) => {
      if (!booking.dateConfirmed || booking.completedAt) return false;
      const last = (booking.sessions || [])
        .map((session) => String(session.shootDate || ''))
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
        .sort()
        .at(-1) || booking.shootDate || '';
      return Boolean(last && last < today);
    })
    .forEach((booking) => {
      const quote = byId.get(booking.quoteId);
      push({
        id: `shot:${booking.quoteId}`,
        reason: 'Shoot date has passed — mark the shoot done',
        detail: shootLine(quote),
        ...forShoot(quote, 'money'),
      });
    });
  liveBookings
    .filter((booking) => booking.deliveredAt)
    .forEach((booking) => {
      const quote = byId.get(booking.quoteId);
      const summary = activePayments.find((row) => row.quoteId === booking.quoteId);
      const paid = Boolean(summary && (summary.outstandingMinor || 0) === 0 && (summary.receivedMinor || 0) > 0);
      push({
        id: `close:${booking.quoteId}`,
        reason: paid ? 'Delivered and paid in full — close the job' : `Delivered — ${formatQuoteMoney(summary?.outstandingMinor || 0, 'R')} still to collect`,
        detail: shootLine(quote),
        ...forShoot(quote, 'money'),
      });
    });
  editingJobs.filter((row) => !row.countdown.overdue && row.countdown.daysLeft !== null && row.countdown.daysLeft <= 3).forEach(({ booking, quote, countdown }) => {
    push({
      id: `deliver:${booking.quoteId}`,
      reason: `Deliver the photographs — ${countdown.text}`,
      detail: shootLine(quote),
      ...forShoot(quote, 'money'),
    });
  });
  const liveQuotes = quotes.filter(operational);
  const draftCount = liveQuotes.filter((quote) => quote.status === 'draft').length;
  if (draftCount > 0) {
    push({
      id: 'drafts',
      client: 'Quotations',
      reason: draftCount === 1 ? '1 quotation still in draft' : `${draftCount} quotations still in draft`,
      section: 'quote-history',
    });
  }

  const bookkeeping: Array<{ id: string; reason: string }> = [];
  if ((billing?.paymentsNotRecordedInZoho || 0) > 0) {
    bookkeeping.push({ id: 'zoho-payments', reason: `${billing?.paymentsNotRecordedInZoho} payment${billing?.paymentsNotRecordedInZoho === 1 ? '' : 's'} not yet recorded in Zoho` });
  }
  if ((billing?.invoicesAwaitingCapture || 0) > 0) {
    bookkeeping.push({ id: 'zoho-invoices', reason: `${billing?.invoicesAwaitingCapture} accepted quotation${billing?.invoicesAwaitingCapture === 1 ? '' : 's'} with no Zoho invoice yet` });
  }

  const openItem = (item: DeskItem) => {
    if (item.quoteId && onOpenShoot) {
      onOpenShoot(item.quoteId, toShootTab(item.tab));
      return;
    }
    onOpen(item.section || 'clients', item.quoteId);
  };
  const upcomingAll = bookings
    .filter((booking) => booking.dateConfirmed && !booking.completedAt && !booking.deletedAt && !booking.archivedAt && !booking.isTest)
    .flatMap((booking) => {
      const sessions = Array.isArray(booking.sessions) ? booking.sessions : [];
      // The overview already returns one row per day, and that row still carries every session.
      if (booking.sessionId || sessions.length < 2) {
        return booking.shootDate && booking.shootDate >= today ? [booking] : [];
      }
      return sessions
        .filter((session) => session.shootDate && session.shootDate >= today)
        .map((session, index) => ({
          ...booking,
          sessionId: session.id || `${booking.quoteId}:${session.shootDate}:${index}`,
          shootDate: session.shootDate,
          startTime: session.startTime || booking.startTime,
          endTime: session.endTime || booking.endTime,
          shootType: session.label || booking.shootType || `Day ${index + 1}`,
        }));
    })
    .sort((left, right) => `${left.shootDate} ${left.startTime}`.localeCompare(`${right.shootDate} ${right.startTime}`));
  const upcoming = upcomingAll.slice(0, 4);
  const openEnquiries = enquiries.filter((row) => !row.linkedQuoteId);
  const shownEnquiries = [...openEnquiries]
    .sort((left, right) => String(right.receivedAt || '').localeCompare(String(left.receivedAt || '')))
    .slice(0, 6);
  const waitingMessages = attention?.awaitingCarmen || 0;
  const shownItems = showAllAttention ? items : items.slice(0, 5);
  const openMoney = (kind: string) => onOpen('payments', kind);
  const scrollTo = (id: string) => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(id)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };
  const openEnquiry = (row: EnquiryRow) => {
    if (row.linkedQuoteId && onOpenShoot) {
      onOpenShoot(row.linkedQuoteId, 'details');
      return;
    }
    if (onOpenEnquiry) onOpenEnquiry(row.id);
    else onOpen('clients');
  };

  // Quick triage: an enquiry that is going nowhere can be dropped from Today
  // without opening it. Two taps — "Remove" then "Confirm" — nothing is sent.
  const removeEnquiry = async (row: EnquiryRow) => {
    if (removingEnquiry) return;
    setRemovingEnquiry(row.id);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/enquiries/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The enquiry could not be removed.');
      setEnquiries((current) => current.filter((item) => item.id !== row.id));
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : 'The enquiry could not be removed.');
    } finally {
      setRemovingEnquiry('');
      setConfirmRemoveId('');
    }
  };

  // One stage per live job, from the same derivation the job file uses.
  const stageCounts: Record<JourneyStage, number> = { enquiry: 0, quoted: 0, accepted: 0, booked: 0, editing: 0, delivered: 0, closed: 0, lost: 0 };
  stageCounts.enquiry += openEnquiries.length;
  liveQuotes.forEach((quote) => {
    const summary = activePayments.find((row) => row.quoteId === quote.id);
    const booking = bookings.find((row) => row.quoteId === quote.id);
    const view = deriveClientJourney(quote as unknown as StoredQuote, summary, booking, undefined, today);
    stageCounts[view.stage] += 1;
  });
  stageCounts.editing = editingJobs.length;
  const overdueDeliveries = editingJobs.filter((row) => row.countdown.overdue).length;
  const openStage = (stage: JourneyStage) => {
    if (stage === 'enquiry') { scrollTo('open-enquiries'); return; }
    if (stage === 'editing') { scrollTo('deliveries-due'); return; }
    onOpen('clients', `stage:${stage}`);
  };

  return (
    <div className="studio-home studio-today">
      <header className="studio-home-head">
        <div>
          <h2>{greeting(now)}</h2>
          <p>{new Intl.DateTimeFormat('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' }).format(now)}</p>
        </div>
        <div className="studio-home-actions">
          <button type="button" className="studio-secondary" onClick={() => (onStartEnquiry ? onStartEnquiry() : onOpen('clients'))}>New enquiry</button>
          <button type="button" className="studio-primary" onClick={() => onOpen('quote-builder')}>New quotation</button>
        </div>
      </header>
      {error ? <p className="studio-error" role="alert">{error} <button type="button" className="studio-text-action" onClick={() => window.location.reload()}>Try again</button></p> : null}
      {loading ? <p className="studio-note">Loading the studio overview…</p> : (
      <>
      <ol className="studio-pipeline" aria-label="Pipeline" data-testid="studio-pipeline">
        {PIPELINE.map((column) => {
          const value = stageCounts[column.stage];
          const isEditing = column.stage === 'editing';
          return (
            <li key={column.stage} className={`is-${column.stage}${value > 0 ? ' is-live' : ''}${isEditing && overdueDeliveries > 0 ? ' is-overdue' : ''}`}>
              <button type="button" onClick={() => openStage(column.stage)} data-testid={`pipeline-${column.stage}`}>
                <small>{column.label}</small>
                <strong className={value === 0 ? 'is-zero' : ''}>{value}</strong>
                <span className="studio-glance-hint">
                  {isEditing && overdueDeliveries > 0
                    ? `${overdueDeliveries} overdue`
                    : column.hint}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      <p className="studio-today-ledger">
        <button type="button" onClick={() => openMoney('all')}>Booked <span className="studio-amount">{formatQuoteMoney(money.agreed, 'R')}</span></button>
        <button type="button" onClick={() => openMoney('received')}>Paid <span className="studio-amount">{formatQuoteMoney(money.received, 'R')}</span></button>
        <button type="button" onClick={() => openMoney('outstanding')}>Still to collect <span className="studio-amount">{formatQuoteMoney(money.outstanding, 'R')}</span></button>
        {money.overdue > 0 ? (
          <button type="button" className="is-overdue" onClick={() => openMoney('overdue')}>Overdue <span className="studio-amount">{formatQuoteMoney(money.overdue, 'R')}</span></button>
        ) : null}
        {waitingMessages > 0 ? (
          <button type="button" onClick={() => onOpen('client-messages')}>{waitingMessages} message{waitingMessages === 1 ? '' : 's'} waiting</button>
        ) : null}
        <button type="button" onClick={() => onOpen('finance')}>This month →</button>
      </p>
          {bookkeeping.length > 0 ? (
            <p className="studio-note">
              {`${bookkeeping.map((row) => row.reason).join('. ')}. `}
              <button type="button" className="studio-text-action" onClick={() => onOpen('payments')}>Open payments</button>
            </p>
          ) : null}
      {items.length > 0 ? (
        <section className="studio-daybook" aria-labelledby="needs-attention">
          <h3 id="needs-attention" className="studio-section-label">Needs you</h3>
          {shownItems.map((item) => (
            <div className="studio-attention-row" key={item.id}>
              <strong>{item.client}</strong>
              <span>
                {item.detail ? <span className="studio-card-facts">{item.detail}</span> : null}
                {withAmounts(item.reason)}
              </span>
              <button type="button" className="studio-text-action" onClick={() => openItem(item)}>Open</button>
            </div>
          ))}
          {items.length > 5 ? (
            <p>
              <button type="button" className="studio-text-action" onClick={() => setShowAllAttention((open) => !open)}>
                {showAllAttention ? 'Show fewer' : `Show ${items.length - 5} more`}
              </button>
            </p>
          ) : null}
        </section>
      ) : null}
      <div className="studio-split">
        <section className="studio-panel" id="open-enquiries" aria-labelledby="open-enquiries-label" data-testid="open-enquiries">
          <h3 id="open-enquiries-label" className="studio-section-label">
            Client details{openEnquiries.length > 0 ? ` · ${openEnquiries.length} need a quotation` : ''}
          </h3>
          {shownEnquiries.length === 0 ? (
            <p className="studio-empty">No enquiries waiting for a quotation.</p>
          ) : shownEnquiries.map((row) => (
            <div className="studio-enquiry-row" key={row.id}>
              <div>
                <strong>{row.name || 'Enquiry'}</strong>
                <p className="studio-card-facts">
                  {todayShootLine({
                    shootType: row.shootType,
                    date: row.preferredDate,
                  }, { fill: true })}
                </p>
                <p>
                  {[
                    row.source,
                    arrived(row.receivedAt) ? `received ${arrived(row.receivedAt)}` : '',
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className="studio-enquiry-actions">
                {confirmRemoveId === row.id ? (
                  <>
                    <button
                      type="button"
                      className="studio-danger"
                      onClick={() => removeEnquiry(row)}
                      disabled={removingEnquiry === row.id}
                      data-testid={`remove-enquiry-confirm-${row.id}`}
                    >
                      {removingEnquiry === row.id ? 'Removing…' : 'Confirm delete'}
                    </button>
                    <button type="button" className="studio-text-action" onClick={() => setConfirmRemoveId('')}>Keep</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="studio-text-action" onClick={() => setConfirmRemoveId(row.id)} data-testid={`remove-enquiry-${row.id}`} title="Delete this enquiry without quoting">Remove</button>
                    <button type="button" className="studio-text-action" onClick={() => openEnquiry(row)}>Open</button>
                  </>
                )}
              </div>
            </div>
          ))}
          {enquiries.length > 0 ? (
            <p>
              <button type="button" className="studio-text-action" onClick={() => onOpen('clients')}>
                All clients
              </button>
            </p>
          ) : null}
        </section>
        <section className="studio-panel" aria-labelledby="upcoming-shoots">
          <h3 id="upcoming-shoots" className="studio-section-label">Next shoots</h3>
          {upcoming.length === 0 ? <p className="studio-empty">No shoots on the calendar.</p> : upcoming.map((booking) => (
            <div className="studio-agenda-row" key={`${booking.quoteId}:${booking.sessionId || booking.shootDate || ''}:${booking.startTime || ''}`}>
              <div>
                <strong>{shortDay(booking.shootDate) || studioDate(booking.shootDate)}</strong>
                <div>{booking.startTime || ''}</div>
              </div>
              <div>
                <button type="button" className="studio-text-action" onClick={() => onOpenShoot?.(booking.quoteId, 'booking')}>
                  <strong>{booking.clientName || 'Client'}</strong>
                </button>
                <div>
                  {booking.shootType || 'Shoot'}{booking.locationName ? ` · ${booking.locationName}` : ''}
                  {booking.mapsUrl && /^https:\/\//i.test(booking.mapsUrl)
                    ? <> · <a href={booking.mapsUrl} target="_blank" rel="noreferrer">Map</a></>
                    : null}
                </div>
              </div>
            </div>
          ))}
          <p><button type="button" className="studio-text-action" onClick={() => onOpen('bookings')}>Calendar</button></p>
        </section>
      </div>
      <div className="studio-split">
        <section
          className={`studio-panel studio-deliveries${overdueDeliveries > 0 ? ' is-overdue' : ''}`}
          id="deliveries-due"
          aria-labelledby="deliveries-due-label"
          data-testid="deliveries-due"
        >
          <h3 id="deliveries-due-label" className="studio-section-label">
            Editing · client waiting{editingJobs.length > 0 ? ` · ${editingJobs.length}` : ''}
          </h3>
          {editingJobs.length === 0 ? (
            <p className="studio-empty">No photographs being edited. Mark a shoot done to start the clock.</p>
          ) : editingJobs.slice(0, 6).map(({ booking, quote, countdown }) => (
            <div className={`studio-agenda-row studio-delivery-row${countdown.overdue ? ' is-overdue' : ''}`} key={booking.quoteId} data-testid="delivery-row">
              <div>
                <strong>{booking.deliveryDueOn ? shortDay(booking.deliveryDueOn) : '—'}</strong>
                <div className="studio-days-left">
                  {countdown.daysLeft === null ? 'no date' : countdown.daysLeft < 0 ? `${Math.abs(countdown.daysLeft)}d over` : countdown.daysLeft === 0 ? 'today' : `${countdown.daysLeft}d left`}
                </div>
              </div>
              <div>
                <button type="button" className="studio-text-action" onClick={() => onOpenShoot?.(booking.quoteId, 'money')}>
                  <strong>{booking.clientName || quote?.client?.name || 'Client'}{booking.isTest ? ' · Test' : ''}</strong>
                </button>
                <div>
                  {booking.shootType || quote?.shootType || 'Shoot'}
                  {booking.shootDate ? ` · shot ${shortDay(booking.shootDate)}` : ''}
                </div>
                <div className="studio-card-facts">{countdown.text}</div>
              </div>
              <button type="button" className="studio-text-action" onClick={() => onOpenShoot?.(booking.quoteId, 'money')}>Deliver</button>
            </div>
          ))}
          {editingJobs.length > 6 ? (
            <p><button type="button" className="studio-text-action" onClick={() => onOpen('clients', 'stage:editing')}>All {editingJobs.length} editing jobs</button></p>
          ) : null}
        </section>
        <section className="studio-panel studio-activity" aria-labelledby="recent-activity" data-testid="recent-activity">
          <h3 id="recent-activity" className="studio-section-label">Recent activity</h3>
          {activity.length === 0 ? <p className="studio-empty">Nothing has happened yet.</p> : activity.slice(0, 10).map((row) => (
            <div className={`studio-activity-row is-${row.actor}`} key={row.id}>
              <span className="studio-activity-when">{timeAgo(row.at, now)}</span>
              <div>
                <button type="button" className="studio-text-action" onClick={() => onOpenShoot?.(row.quoteId, toShootTab(row.tab))}>
                  <strong>{row.clientName}</strong>
                </button>
                <span> {row.title}</span>
                {row.detail ? <div className="studio-card-facts">{row.detail}</div> : null}
              </div>
            </div>
          ))}
        </section>
      </div>
      <section
        className={reminders.length > 0 ? 'studio-daybook' : 'studio-today-quiet'}
        aria-labelledby="suggested-reminders"
        data-testid="suggested-reminders"
      >
        <h3 id="suggested-reminders" className="studio-section-label">Suggested reminders</h3>
        {reminderNote ? <p className="studio-note">{reminderNote}</p> : null}
        {reminders.length === 0 ? <p className="studio-empty">None to send.</p> : null}
        {reminders.map((row) => (
          <div className={`studio-attention-row${row.internal ? ' is-internal' : ''}${row.overdue ? ' is-overdue' : ''}`} key={row.id} data-testid={`reminder-${row.category}`}>
            <strong>{row.clientName}</strong>
            <span>{row.label} — {row.reason}</span>
            <div className="studio-toolbar">
              <button type="button" className="studio-primary" onClick={() => openReminderShoot(row)}>
                {row.internal
                  ? (row.action === 'deliver' ? 'Deliver photos' : row.action === 'complete' ? 'Mark shoot done' : 'Open')
                  : 'Open to send'}
              </button>
              <button type="button" className="studio-secondary" onClick={() => actOnReminder('snooze', row)}>Snooze</button>
              <button type="button" className="studio-text-action" onClick={() => actOnReminder('dismiss', row)}>Dismiss</button>
            </div>
          </div>
        ))}
      </section>
      </>
      )}
    </div>
  );
};

export default Dashboard;
